import { logger } from '../utils/logger';
// 认证调试工具
// 用于诊断认证状态和token问题

export const debugAuthState = () => {
  logger.log('=== 认证状态调试信息 ===');
  
  // 检查localStorage中的token
  const accessToken = localStorage.getItem('cognito_access_token');
  const idToken = localStorage.getItem('cognito_id_token');
  const refreshToken = localStorage.getItem('cognito_refresh_token');
  
  logger.log('Token状态:');
  logger.log('- Access Token:', accessToken ? '存在' : '不存在');
  logger.log('- ID Token:', idToken ? '存在' : '不存在');
  logger.log('- Refresh Token:', refreshToken ? '存在' : '不存在');
  
  // 检查sessionStorage中的OAuth状态
  const oauthState = sessionStorage.getItem('oauth_state');
  const oauthProcessing = sessionStorage.getItem('oauth_processing');
  const processedCode = sessionStorage.getItem('processed_oauth_code');
  
  logger.log('OAuth状态:');
  logger.log('- OAuth State:', oauthState || '不存在');
  logger.log('- OAuth Processing:', oauthProcessing || '不存在');
  logger.log('- Processed Code:', processedCode || '不存在');
  
  // 检查URL参数
  const urlParams = new URLSearchParams(window.location.search);
  const code = urlParams.get('code');
  const state = urlParams.get('state');
  const error = urlParams.get('error');
  
  logger.log('URL参数:');
  logger.log('- Code:', code || '不存在');
  logger.log('- State:', state || '不存在');
  logger.log('- Error:', error || '不存在');
  
  // 检查当前页面信息
  logger.log('页面信息:');
  logger.log('- 当前URL:', window.location.href);
  logger.log('- Origin:', window.location.origin);
  logger.log('- Pathname:', window.location.pathname);
  
  // 计算认证状态
  const isAuthenticated = !!(accessToken && idToken);
  logger.log('=== 认证状态计算结果 ===');
  logger.log('是否已认证:', isAuthenticated);
  
  return {
    isAuthenticated,
    hasAccessToken: !!accessToken,
    hasIdToken: !!idToken,
    hasRefreshToken: !!refreshToken,
    oauthState,
    oauthProcessing,
    processedCode,
    urlCode: code,
    urlState: state,
    urlError: error
  };
};

// 清除所有认证相关的存储
export const clearAuthStorage = () => {
  logger.log('=== 清除认证存储 ===');
  
  // 清除localStorage
  localStorage.removeItem('cognito_access_token');
  localStorage.removeItem('cognito_id_token');
  localStorage.removeItem('cognito_refresh_token');
  
  // 清除sessionStorage
  sessionStorage.removeItem('oauth_state');
  sessionStorage.removeItem('oauth_processing');
  sessionStorage.removeItem('processed_oauth_code');
  
  logger.log('所有认证存储已清除');
};

// 验证token有效性
export const validateTokens = async () => {
  logger.log('=== 验证Token有效性 ===');
  
  const accessToken = localStorage.getItem('cognito_access_token');
  const idToken = localStorage.getItem('cognito_id_token');
  
  if (!accessToken || !idToken) {
    logger.log('Token不存在，无法验证');
    return { valid: false, reason: 'Token不存在' };
  }
  
  try {
    // 解析JWT token（不验证签名，只检查格式和过期时间）
    const parseJWT = (token: string) => {
      try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));
        return JSON.parse(jsonPayload);
      } catch (error) {
        logger.error('Token解析失败:', error);
        return null;
      }
    };
    
    const accessTokenPayload = parseJWT(accessToken);
    const idTokenPayload = parseJWT(idToken);
    
    if (!accessTokenPayload || !idTokenPayload) {
      return { valid: false, reason: 'Token格式无效' };
    }
    
    const now = Math.floor(Date.now() / 1000);
    
    logger.log('Access Token信息:');
    logger.log('- 过期时间:', new Date(accessTokenPayload.exp * 1000).toLocaleString());
    logger.log('- 是否过期:', accessTokenPayload.exp < now);
    
    logger.log('ID Token信息:');
    logger.log('- 过期时间:', new Date(idTokenPayload.exp * 1000).toLocaleString());
    logger.log('- 是否过期:', idTokenPayload.exp < now);
    
    const isAccessTokenExpired = accessTokenPayload.exp < now;
    const isIdTokenExpired = idTokenPayload.exp < now;
    
    if (isAccessTokenExpired || isIdTokenExpired) {
      return { 
        valid: false, 
        reason: 'Token已过期',
        accessTokenExpired: isAccessTokenExpired,
        idTokenExpired: isIdTokenExpired
      };
    }
    
    return { 
      valid: true, 
      accessTokenPayload, 
      idTokenPayload 
    };
    
  } catch (error) {
    logger.error('Token验证失败:', error);
    return { valid: false, reason: 'Token验证异常' };
  }
};

// 在控制台输出完整的认证调试信息
export const logFullAuthDebug = async () => {
  logger.log('=== 完整认证调试信息 ===');
  
  const authState = debugAuthState();
  const tokenValidation = await validateTokens();
  
  logger.log('=== 总结 ===');
  logger.log('认证状态:', authState.isAuthenticated ? '已认证' : '未认证');
  logger.log('Token有效性:', tokenValidation.valid ? '有效' : '无效');
  
  if (!tokenValidation.valid) {
    logger.log('Token无效原因:', tokenValidation.reason);
  }
  
  // 提供修复建议
  if (!authState.isAuthenticated) {
    logger.log('=== 修复建议 ===');
    if (!authState.hasAccessToken || !authState.hasIdToken) {
      logger.log('1. Token不存在，需要重新登录');
    } else if (!tokenValidation.valid) {
      logger.log('2. Token已过期，需要刷新或重新登录');
    }
  }
  
  return {
    authState,
    tokenValidation
  };
};

// 在window对象上暴露调试函数，方便在控制台调用
if (typeof window !== 'undefined') {
  (window as any).debugAuth = {
    debugAuthState,
    clearAuthStorage,
    validateTokens,
    logFullAuthDebug
  };
  
  logger.log('认证调试工具已加载，可在控制台使用:');
  logger.log('- window.debugAuth.debugAuthState() - 检查认证状态');
  logger.log('- window.debugAuth.clearAuthStorage() - 清除认证存储');
  logger.log('- window.debugAuth.validateTokens() - 验证token有效性');
  logger.log('- window.debugAuth.logFullAuthDebug() - 输出完整调试信息');
}
